import React, { useState, useEffect, useMemo, useRef } from "react";

/* ───────────────────────────────────────────────────────────────────────────
   Free Immigration — a freelance marketplace connecting people with vetted
   immigration lawyers. Single-file React, Netlify-ready.

   Backend: /immigration on the shared Heroku instance.
   • Public site + open AI chat (no signup)
   • OTP signup → saves case → matched lawyers → message them
   • Lawyer portal:  ?lawyer=true
   • Admin console:  ?admin=true   (master code 123123 / 999999)
─────────────────────────────────────────────────────────────────────────── */

const API = "https://learntok-backend-2026-24c204fe508e.herokuapp.com/immigration";
const ADMIN_KEY = "imm-admin-2026";
const UTOK = "fi_user_token";
const LTOK = "fi_lawyer_token";

const GOALS = [
  { label: "Work in the U.S.", desc: "Job offer, transfer, or special talent" },
  { label: "Join family", desc: "Spouse, parent, child, or fiancé(e)" },
  { label: "Get a green card", desc: "Permanent residency" },
  { label: "Study", desc: "University, exchange, or training" },
  { label: "Invest or start a business", desc: "Founder or investor routes" },
  { label: "Seek protection", desc: "Asylum or humanitarian relief" },
  { label: "I'm not sure yet", desc: "Help me figure out my options" },
];
const STATUSES = ["New", "Reviewing", "Info needed", "Matched", "Closed"];

/* ── api helper ─────────────────────────────────────────────────────────────── */
async function api(path, { method = "GET", token, body } = {}) {
  const r = await fetch(API + path, {
    method,
    headers: { "content-type": "application/json", ...(token ? { "x-token": token } : {}) },
    body: body ? JSON.stringify(body) : undefined,
  });
  try { return await r.json(); } catch { return {}; }
}
const ls = {
  get: (k) => { try { return localStorage.getItem(k); } catch { return null; } },
  set: (k, v) => { try { localStorage.setItem(k, v); } catch {} },
  del: (k) => { try { localStorage.removeItem(k); } catch {} },
};

/* ── style system ───────────────────────────────────────────────────────────── */
const CSS = `
:root{
  --ink:#122E27; --ink2:#1C3B33; --paper:#FAF7F0; --paper2:#F1ECE0;
  --amber:#E0973B; --amber-d:#C97F26; --stamp:#B23A2E;
  --line:rgba(18,46,39,.14); --muted:#5A6B63;
}
*{box-sizing:border-box}
html,body,#root{margin:0;background:var(--paper);color:var(--ink)}
body{font-family:Inter,system-ui,sans-serif;-webkit-font-smoothing:antialiased;overflow-x:hidden}
::selection{background:var(--amber);color:var(--ink)}
.wrap{max-width:1060px;margin:0 auto;padding:0 22px}
.mono{font-family:'Space Mono',ui-monospace,monospace}
.serif{font-family:Spectral,Georgia,serif}
.eyebrow{font-family:'Space Mono',monospace;font-size:12px;letter-spacing:.16em;
  text-transform:uppercase;color:var(--amber-d)}
a{color:inherit}
button{font-family:inherit;cursor:pointer}

.nav{display:flex;align-items:center;justify-content:space-between;padding:22px 0;gap:10px;flex-wrap:wrap}
.brand{display:flex;align-items:center;gap:10px;font-family:Spectral;font-weight:600;font-size:22px}
.seal{width:30px;height:30px;border:2px solid var(--ink);border-radius:50%;
  display:grid;place-items:center;font-family:'Space Mono',monospace;font-size:11px;
  font-weight:700;transform:rotate(-8deg)}
.navlink{font-size:14px;color:var(--muted);text-decoration:none;background:none;border:none}
.navactions{display:flex;gap:14px;align-items:center}

.hero{padding:30px 0 56px}
.h1{font-family:Spectral;font-weight:600;line-height:1.02;letter-spacing:-.01em;
  font-size:clamp(34px,6vw,62px);margin:18px 0 14px;max-width:14ch}
.h1 em{font-style:italic;color:var(--amber-d)}
.lede{font-size:clamp(16px,2.2vw,19px);color:var(--ink2);max-width:46ch;line-height:1.55}
.trust{display:flex;gap:26px;flex-wrap:wrap;margin-top:26px;padding-top:22px;border-top:1px solid var(--line)}
.trust b{font-family:Spectral;font-size:24px;display:block}
.trust span{font-size:12px;color:var(--muted)}

.panel{background:#fff;border:1px solid var(--line);border-radius:18px;padding:26px;
  box-shadow:0 24px 60px -38px rgba(18,46,39,.45)}
.panel-h{font-family:Spectral;font-size:20px;margin:0 0 4px}
.panel-s{font-size:13px;color:var(--muted);margin:0 0 18px}
.opt{display:flex;flex-direction:column;gap:2px;text-align:left;width:100%;
  border:1px solid var(--line);background:var(--paper);border-radius:12px;padding:13px 15px;
  margin-bottom:9px;transition:.16s;color:var(--ink)}
.opt:hover{border-color:var(--ink);background:#fff;transform:translateX(3px)}
.opt b{font-weight:600;font-size:15px}
.opt small{color:var(--muted);font-size:12.5px}
.opt.sel{border-color:var(--amber-d);background:#fff;box-shadow:inset 0 0 0 1px var(--amber-d)}
.heroGrid{display:grid;grid-template-columns:1.05fr .95fr;gap:44px;align-items:center}
@media(max-width:860px){.heroGrid{grid-template-columns:1fr;gap:30px}}

.btn{background:var(--ink);color:var(--paper);border:none;border-radius:11px;
  padding:14px 22px;font-weight:600;font-size:15px;transition:.16s}
.btn:hover{background:#0a1d18}
.btn:disabled{opacity:.45;cursor:not-allowed}
.btn.amber{background:var(--amber);color:var(--ink)}
.btn.amber:hover{background:var(--amber-d)}
.btn.ghost{background:transparent;color:var(--muted);box-shadow:none;padding-left:0;padding-right:0}
.btn.ghost:hover{color:var(--ink)}
.btn.sm{padding:9px 14px;font-size:13px;border-radius:9px}
.btn.line{background:#fff;color:var(--ink);border:1px solid var(--ink)}

.bar{height:5px;background:var(--paper2);border-radius:99px;overflow:hidden;margin:4px 0 22px}
.bar i{display:block;height:100%;background:var(--amber);transition:width .4s ease}

.stage{min-height:100vh;display:flex;flex-direction:column;justify-content:center;padding:40px 0}
.qh{font-family:Spectral;font-size:clamp(24px,4vw,34px);line-height:1.12;margin:6px 0 6px;overflow-wrap:break-word}
.qhelp{color:var(--muted);font-size:15px;margin:0 0 24px;max-width:52ch}
.qgrid{display:grid;gap:10px}
.otherInput{width:100%;border:1px solid var(--line);border-radius:11px;padding:13px 15px;
  font-size:15px;font-family:inherit;background:#fff;margin-top:6px}
.otherInput:focus{outline:none;border-color:var(--ink)}
.flowfoot{display:flex;justify-content:space-between;align-items:center;margin-top:26px}

.dots span{display:inline-block;width:7px;height:7px;border-radius:50%;background:var(--amber);
  margin:0 3px;animation:bnc 1s infinite}
.dots span:nth-child(2){animation-delay:.15s}.dots span:nth-child(3){animation-delay:.3s}
@keyframes bnc{0%,80%,100%{transform:translateY(0);opacity:.4}40%{transform:translateY(-7px);opacity:1}}

.stamp{display:inline-block;border:3px double var(--stamp);color:var(--stamp);
  padding:12px 18px;transform:rotate(-5deg);font-family:'Space Mono',monospace;
  text-align:center;line-height:1.3;border-radius:4px;animation:thud .5s cubic-bezier(.2,1.4,.4,1)}
.stamp b{display:block;font-size:18px;letter-spacing:.12em}
.stamp small{font-size:11px;letter-spacing:.18em}
@keyframes thud{0%{transform:rotate(-5deg) scale(2.2);opacity:0}60%{opacity:1}100%{transform:rotate(-5deg) scale(1)}}
.cat{border:1px solid var(--line);border-radius:12px;padding:14px 16px;background:#fff;margin-bottom:10px}
.cat b{font-family:Spectral;font-size:17px}.cat p{margin:3px 0 0;color:var(--muted);font-size:14px}
.signal{display:inline-block;font-family:'Space Mono',monospace;font-size:12px;
  padding:6px 12px;border-radius:99px;letter-spacing:.06em;text-transform:uppercase}

.steps{display:grid;grid-template-columns:repeat(3,1fr);gap:18px;margin-top:24px}
@media(max-width:760px){.steps{grid-template-columns:1fr}}
.step{border-top:2px solid var(--ink);padding-top:14px}
.step .n{font-family:'Space Mono',monospace;font-size:13px;color:var(--amber-d)}
.step h4{font-family:Spectral;font-size:18px;margin:6px 0 5px}
.step p{margin:0;color:var(--muted);font-size:14px;line-height:1.5}

.field{margin-bottom:14px}
.field label{display:block;font-size:13px;color:var(--muted);margin-bottom:5px}
.field input,.field textarea{width:100%;border:1px solid var(--line);border-radius:11px;padding:13px 15px;
  font-size:15px;font-family:inherit;background:#fff}
.field input:focus,.field textarea:focus{outline:none;border-color:var(--ink)}

.section{padding:66px 0;border-top:1px solid var(--line)}
.kick{font-family:Spectral;font-size:clamp(24px,4vw,34px);margin:0 0 6px}

.offer{border:1px solid var(--amber-d);background:#fff;border-radius:14px;padding:18px 20px;
  margin:18px 0;position:relative}
.offer:before{content:"80% OFF";position:absolute;top:-11px;right:16px;background:var(--amber);
  color:var(--ink);font-family:'Space Mono',monospace;font-size:11px;font-weight:700;
  letter-spacing:.1em;padding:4px 10px;border-radius:99px}
.offer-lead{margin:6px 0 0;font-size:15px;line-height:1.55}
.offer-strong{margin:10px 0 0;font-size:14px;color:var(--ink2);border-top:1px solid var(--line);padding-top:10px}

/* hero badges + guarantee */
.badges{display:flex;gap:10px;flex-wrap:wrap;margin-top:24px}
.badge{display:flex;align-items:center;gap:7px;font-size:13.5px;border:1px solid var(--line);
  background:#fff;border-radius:99px;padding:8px 14px}
.badge b{font-weight:600}
.guarantee{border-radius:18px;padding:28px;background:var(--ink);color:var(--paper);
  display:flex;gap:24px;align-items:center;flex-wrap:wrap}
.seal2{width:66px;height:66px;border:2px solid var(--amber);border-radius:50%;display:grid;
  place-items:center;color:var(--amber);font-family:'Space Mono',monospace;font-weight:700;font-size:12px;
  text-align:center;line-height:1.15;transform:rotate(-6deg);flex:none}
.guarantee h3{font-family:Spectral;font-size:25px;margin:0 0 6px}
.guarantee p{margin:0;color:rgba(250,247,240,.82);font-size:15px;line-height:1.55;max-width:62ch}

/* chat */
.fullcol{max-width:720px;margin:0 auto;width:100%;display:flex;flex-direction:column;min-height:100vh}
.chatHead{display:flex;align-items:center;justify-content:space-between;padding:16px 0;border-bottom:1px solid var(--line)}
.bubbles{flex:1;overflow-y:auto;padding:20px 0;display:flex;flex-direction:column;gap:12px}
.bub{max-width:82%;padding:12px 15px;border-radius:16px;font-size:15px;line-height:1.5;white-space:pre-wrap}
.bub.ai{background:#fff;border:1px solid var(--line);align-self:flex-start;border-bottom-left-radius:5px}
.bub.me{background:var(--ink);color:var(--paper);align-self:flex-end;border-bottom-right-radius:5px}
.composer{display:flex;gap:8px;padding:14px 0 22px;border-top:1px solid var(--line);position:sticky;bottom:0;background:var(--paper)}
.composer input{flex:1;border:1px solid var(--line);border-radius:12px;padding:13px 15px;font-size:15px;font-family:inherit;background:#fff}
.composer input:focus{outline:none;border-color:var(--ink)}

/* lawyer cards */
.lgrid{display:grid;grid-template-columns:repeat(auto-fill,minmax(258px,1fr));gap:14px}
.lcard{border:1px solid var(--line);background:#fff;border-radius:16px;padding:18px;display:flex;flex-direction:column}
.avatar{width:46px;height:46px;border-radius:50%;display:grid;place-items:center;color:#fff;
  font-family:'Space Mono',monospace;font-weight:700;font-size:16px;flex:none}
.stars{font-size:13px;color:var(--amber-d);font-family:'Space Mono',monospace}
.chip{display:inline-block;font-size:11px;font-family:'Space Mono',monospace;background:var(--paper2);
  border-radius:99px;padding:3px 9px;margin:4px 4px 0 0}

/* threads list */
.tcard{border:1px solid var(--line);background:#fff;border-radius:14px;padding:14px 16px;margin-bottom:10px;
  display:flex;gap:12px;align-items:center;text-align:left;width:100%}
.tcard:hover{border-color:var(--ink)}
.casebox{background:var(--paper2);border-radius:12px;padding:12px 14px;font-size:13px;margin:12px 0;line-height:1.5}

.tabs{display:flex;gap:6px;margin:14px 0}
.tab{padding:8px 14px;border-radius:99px;border:1px solid var(--line);background:#fff;font-size:14px}
.tab.on{background:var(--ink);color:var(--paper);border-color:var(--ink)}

.row{border:1px solid var(--line);border-radius:14px;background:#fff;padding:16px;margin-bottom:12px}
.row .top{display:flex;justify-content:space-between;align-items:flex-start;gap:12px}
.tag{font-family:'Space Mono',monospace;font-size:11px;padding:4px 9px;border-radius:6px;background:var(--paper2)}
.qa{font-size:13.5px;color:var(--ink2);border-left:2px solid var(--line);padding-left:12px;margin:8px 0}
.adminbar{display:flex;gap:8px;flex-wrap:wrap;margin:18px 0;align-items:center}
.pill{font-family:'Space Mono',monospace;font-size:12px;border:1px solid var(--line);border-radius:99px;padding:6px 13px;background:#fff}
select,textarea{font-family:inherit;border:1px solid var(--line);border-radius:9px;padding:9px;background:#fff}

.foot{padding:40px 0;border-top:1px solid var(--line);color:var(--muted);font-size:13px}
@media (prefers-reduced-motion:reduce){*{animation:none!important;transition:none!important}}
`;

function useFonts() {
  useEffect(() => {
    const l = document.createElement("link");
    l.rel = "stylesheet";
    l.href = "https://fonts.googleapis.com/css2?family=Spectral:ital,wght@0,400;0,600;1,500&family=Inter:wght@400;500;600&family=Space+Mono:wght@400;700&display=swap";
    document.head.appendChild(l);
    document.title = "Free Immigration — talk to AI, get matched with a lawyer";
  }, []);
}

function captureSource() {
  try {
    const sp = new URLSearchParams(window.location.search);
    const keys = ["utm_source", "utm_medium", "utm_campaign", "src", "ref", "source", "fbclid", "gclid"];
    const parts = [];
    keys.forEach((k) => { const v = sp.get(k); if (v) parts.push(`${k}=${v}`); });
    if (parts.length) return parts.join(" ");
    const r = document.referrer;
    if (r) { try { return "ref:" + new URL(r).hostname; } catch { return "ref:" + r; } }
    return "direct";
  } catch { return "direct"; }
}

const Logo = () => (
  <div className="brand"><span className="seal">FI</span> Free Immigration</div>
);
const Avatar = ({ p }) => (
  <div className="avatar" style={{ background: p?.color || "#5A6B63" }}>{p?.initials || "?"}</div>
);
const signalColor = (s = "") =>
  /strong/i.test(s) ? { color: "#1f6b4e", bg: "#dff0e6" }
  : /promising/i.test(s) ? { color: "#9a6a12", bg: "#f6ecd4" }
  : { color: "#7a4b1e", bg: "#f0e4d6" };

/* ───────────────────────────────────────────────────────────────── ROUTER ── */
export default function App() {
  const route = useMemo(() => {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("admin") === "true") return "admin";
    if (sp.get("lawyer") === "true") return "lawyer";
    return "site";
  }, []);
  if (route === "admin") return <Admin />;
  if (route === "lawyer") return <LawyerPortal />;
  return <Site />;
}

/* ─────────────────────────────────────────────────────────────────── SITE ── */
function Site() {
  useFonts();
  const source = useMemo(captureSource, []);
  const [screen, setScreen] = useState("landing"); // landing|flow|chat|signup|dashboard|thread
  const [token, setToken] = useState(ls.get(UTOK) || "");
  const [user, setUser] = useState(null);

  // quiz / intake state shared with signup
  const [goal, setGoal] = useState("Asked the AI assistant");
  const [history, setHistory] = useState([]);
  const [assessment, setAssessment] = useState(null);
  const [question, setQuestion] = useState(null);
  const [loading, setLoading] = useState(false);

  // dashboard data from signup
  const [matched, setMatched] = useState([]);
  const [ref, setRef] = useState("");
  const [activeThread, setActiveThread] = useState(null);
  const deepThread = useMemo(() => new URLSearchParams(window.location.search).get("thread") || "", []);

  useEffect(() => {
    if (!deepThread) return;
    if (token) { setActiveThread(deepThread); setScreen("thread"); }
    else setScreen("login");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!token) return;
    api("/me", { token }).then((d) => {
      if (d?.role === "user") setUser(d.user);
      else { ls.del(UTOK); setToken(""); }
    });
  }, [token]);

  async function fetchNext(g, hist) {
    setLoading(true);
    try {
      const data = await api("/next", { method: "POST", body: { goal: g, history: hist, count: hist.length } });
      if (data.done) { setAssessment(data.assessment); setScreen("signup"); }
      else setQuestion(data.question);
    } catch {
      setQuestion({ text: "Where are you right now?", type: "single", allowOther: true,
        options: [{ label: "Inside the U.S." }, { label: "Outside the U.S." }] });
    } finally { setLoading(false); }
  }
  function startGoal(g) { setGoal(g); setHistory([]); setScreen("flow"); fetchNext(g, []); }
  function connectToLawyer() { setScreen("connect"); }
  function startConnect(questions) {
    setGoal("Connect with a lawyer");
    setHistory([{ question: "Client's questions for a lawyer", answer: questions.join("  •  ") }]);
    setAssessment({
      signal: "Needs a closer look",
      headline: "Wants to connect with a lawyer.",
      summary: questions.join("  •  ").slice(0, 280),
    });
    setScreen("signup");
  }
  function answer(ans) {
    const hist = [...history, { question: question.text, answer: ans }];
    setHistory(hist); setQuestion(null); fetchNext(goal, hist);
  }
  function back() {
    if (history.length === 0) return setScreen("landing");
    const hist = history.slice(0, -1); setHistory(hist); setQuestion(null); fetchNext(goal, hist);
  }

  // chat -> save case
  function saveFromChat({ goal: g, history: h, assessment: a }) {
    setGoal(g); setHistory(h); setAssessment(a); setScreen("signup");
  }
  function onSignedUp({ token: t, user: u, lawyers, ref: r, assessment: a }) {
    ls.set(UTOK, t); setToken(t); setUser(u);
    setMatched(lawyers || []); setRef(r); if (a) setAssessment(a);
    setScreen("dashboard");
  }
  function logout() { ls.del(UTOK); setToken(""); setUser(null); setScreen("landing"); }

  if (screen === "flow")
    return <Flow q={question} loading={loading} onAnswer={answer} onBack={back} step={history.length + 1} />;
  if (screen === "connect")
    return <Connect onContinue={startConnect} onBack={() => setScreen("landing")} />;
  if (screen === "chat")
    return <Chat onBack={() => setScreen("landing")} onSave={saveFromChat} loggedIn={!!user}
                 onDashboard={() => setScreen("dashboard")} />;
  if (screen === "signup")
    return <Signup goal={goal} history={history} assessment={assessment} source={source}
                   onDone={onSignedUp} onBack={() => setScreen("landing")} />;
  if (screen === "dashboard")
    return <Dashboard token={token} user={user} matched={matched} ref_={ref} assessment={assessment}
                      onOpenThread={(id) => { setActiveThread(id); setScreen("thread"); }}
                      onHome={() => setScreen("landing")} onLogout={logout} />;
  if (screen === "login")
    return <ReturningLogin
      onDone={({ token: t, user: u }) => {
        ls.set(UTOK, t); setToken(t); setUser(u);
        if (deepThread) { setActiveThread(deepThread); setScreen("thread"); }
        else setScreen("dashboard");
      }}
      onBack={() => setScreen("landing")} />;
  if (screen === "thread")
    return <Thread token={token} threadId={activeThread} role="user"
                   onBack={() => setScreen("dashboard")} />;

  return <Landing onStart={startGoal} onChat={() => setScreen("chat")} onConnect={connectToLawyer}
                  user={user} onDashboard={() => setScreen("dashboard")} onLogout={logout} />;
}

/* ── Landing ──────────────────────────────────────────────────────────────── */
function Landing({ onStart, onChat, onConnect, user, onDashboard, onLogout }) {
  const [sel, setSel] = useState(null);
  const [lawyers, setLawyers] = useState([]);
  useEffect(() => { api("/lawyers").then((d) => setLawyers((d.lawyers || []).slice(0, 4))); }, []);

  return (
    <>
      <style>{CSS}</style>
      <div className="wrap">
        <nav className="nav">
          <Logo />
          <div className="navactions">
            {user
              ? <><button className="navlink" onClick={onDashboard}>My dashboard</button>
                  <button className="navlink" onClick={onLogout}>Log out</button></>
              : <a className="navlink" href="?lawyer=true">For lawyers →</a>}
          </div>
        </nav>

        <header className="hero">
          <div className="heroGrid">
            <div>
              <span className="eyebrow">A marketplace of vetted immigration lawyers</span>
              <h1 className="h1">Get <em>Free Immigration</em> help!</h1>
              <p className="lede">
                Answer a few questions, then get matched with the best lawyer for your case —
                backed by a refund if it isn't approved.
              </p>
              <div className="badges">
                <span className="badge">★ <b>Best-matched lawyer</b></span>
                <span className="badge"><b>80% off</b> legal fees</span>
                <span className="badge">↩ <b>Refund</b> if not approved</span>
              </div>
            </div>

            <div>
              <button className="btn" style={{ width: "100%" }} onClick={onConnect}>
                Connect to a lawyer →
              </button>
              <div style={{ textAlign: "center", fontSize: 12, color: "var(--muted)", margin: "12px 0" }}>
                or answer a few quick questions
              </div>
              <div className="panel">
              <p className="eyebrow" style={{ marginBottom: 10 }}>Quick check · Step 01</p>
              <h3 className="panel-h">What are you hoping to do?</h3>
              <p className="panel-s">Pick the closest one. You can add detail next.</p>
              {GOALS.map((g) => (
                <button key={g.label} className={"opt" + (sel === g.label ? " sel" : "")}
                  onClick={() => setSel(g.label)}>
                  <b>{g.label}</b><small>{g.desc}</small>
                </button>
              ))}
              <button className="btn amber" style={{ width: "100%", marginTop: 8 }}
                disabled={!sel} onClick={() => onStart(sel)}>
                See if I qualify (free) →
              </button>
            </div>
            </div>
          </div>
        </header>

        {lawyers.length > 0 && (
          <section className="section">
            <span className="eyebrow">The marketplace</span>
            <h2 className="kick">Find the best lawyer for your case.</h2>
            <p className="lede" style={{ marginBottom: 22 }}>
              Compare independent, vetted immigration attorneys by specialty, rating, and rate.
              Save your case and the right ones reach out to you. Every case is backed by our approval guarantee.
            </p>
            <div className="lgrid">
              {lawyers.map((l) => <LawyerCard key={l.id} l={l} />)}
            </div>
          </section>
        )}

        <section className="section">
          <div className="guarantee">
            <div className="seal2">100%<br />BACK</div>
            <div>
              <h3>Refund if your case isn't approved.</h3>
              <p>
                We stand behind the match. Hire a lawyer through Free Immigration and if your
                case isn't approved, you get your legal fees back. The best lawyer, with zero risk.
              </p>
            </div>
          </div>
        </section>

        <section className="section">
          <span className="eyebrow">How it works</span>
          <h2 className="kick">Three steps, no cost to start.</h2>
          <div className="steps">
            <div className="step"><div className="n">01 — Ask or answer</div>
              <h4>AI, free</h4><p>Chat with our AI or take the quick quiz to see which routes fit.</p></div>
            <div className="step"><div className="n">02 — Save your case</div>
              <h4>We match you</h4><p>Verify your number and we line up vetted lawyers for your situation.</p></div>
            <div className="step"><div className="n">03 — Lawyers reach out</div>
              <h4>80% off + refund</h4><p>Matched lawyers see your profile and message you. Representation is 80% off — and refunded if your case isn't approved.</p></div>
          </div>
        </section>

        <footer className="foot">
          <Logo />
          <p style={{ marginTop: 12, maxWidth: "70ch" }}>
            Free Immigration is a marketplace that connects people with independent immigration
            lawyers. The AI and case review are general information, not legal advice, and do not
            by themselves create an attorney–client relationship. The refund guarantee applies to
            legal fees for cases handled through the platform and is subject to terms.
            <a href="?lawyer=true"> Are you a lawyer? Join here.</a>
          </p>
        </footer>
      </div>
    </>
  );
}

function LawyerCard({ l, onMessage, busy }) {
  return (
    <div className="lcard">
      <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
        <Avatar p={l} />
        <div>
          <b className="serif" style={{ fontSize: 17 }}>{l.name}</b>
          <div style={{ fontSize: 12.5, color: "var(--muted)" }}>{l.location}</div>
        </div>
      </div>
      <div style={{ fontSize: 13.5, marginTop: 10, color: "var(--ink2)" }}>{l.title}</div>
      <div className="stars" style={{ marginTop: 6 }}>★ {l.rating?.toFixed?.(1) || l.rating}</div>
      <div style={{ marginTop: 8 }}>{(l.specialties || []).map((s) => <span className="chip" key={s}>{s}</span>)}</div>
      <div style={{ fontSize: 12.5, color: "var(--muted)", marginTop: 10, flex: 1 }}>{l.bio}</div>
      <div style={{ fontSize: 12.5, marginTop: 10, fontFamily: "'Space Mono',monospace" }}>{l.rate}</div>
      {onMessage && (
        <button className="btn amber sm" style={{ marginTop: 12 }} disabled={busy} onClick={() => onMessage(l.id)}>
          Message {l.name.split(" ")[0]}
        </button>
      )}
    </div>
  );
}

/* ── Flow ─────────────────────────────────────────────────────────────────── */
function Flow({ q, loading, onAnswer, onBack, step }) {
  const [sel, setSel] = useState(null);
  const [other, setOther] = useState("");
  useEffect(() => { setSel(null); setOther(""); }, [q]);
  const pct = Math.min(90, step * 14);
  const otherChosen = sel === "__other__";
  const valid = sel && (!otherChosen || other.trim());
  const finalAnswer = otherChosen ? other.trim() : sel;

  return (
    <>
      <style>{CSS}</style>
      <div className="wrap stage">
        <div style={{ maxWidth: 620, width: "100%", margin: "0 auto" }}>
          <p className="eyebrow">Quick check · Step {String(step + 1).padStart(2, "0")}</p>
          <div className="bar"><i style={{ width: pct + "%" }} /></div>
          {loading || !q ? (
            <div style={{ padding: "40px 0" }}>
              <div className="dots"><span /><span /><span /></div>
              <p className="qhelp" style={{ marginTop: 14 }}>Reading your answers…</p>
            </div>
          ) : (
            <>
              <h2 className="qh">{q.text}</h2>
              {q.help && <p className="qhelp">{q.help}</p>}
              <div className="qgrid">
                {(q.options || []).map((o, i) => (
                  <button key={i} className={"opt" + (sel === o.label ? " sel" : "")} onClick={() => setSel(o.label)}>
                    <b>{o.label}</b>{o.desc && <small>{o.desc}</small>}
                  </button>
                ))}
                {q.allowOther && (
                  <button className={"opt" + (otherChosen ? " sel" : "")} onClick={() => setSel("__other__")}>
                    <b>Something else / I want to explain</b>
                  </button>
                )}
              </div>
              {otherChosen && (
                <input className="otherInput" autoFocus placeholder="Tell us in your own words…"
                  value={other} onChange={(e) => setOther(e.target.value)} />
              )}
              <div className="flowfoot">
                <button className="btn ghost" onClick={onBack}>← Back</button>
                <button className="btn amber" disabled={!valid} onClick={() => onAnswer(finalAnswer)}>Continue</button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

/* ── Open AI chat (no signup) ─────────────────────────────────────────────── */
function Chat({ onBack, onSave, loggedIn, onDashboard }) {
  const [msgs, setMsgs] = useState([
    { role: "assistant", content: "Hi! I'm the Free Immigration assistant. Tell me about your situation — what are you hoping to do in the U.S.? I can suggest which visas or green cards might fit." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, busy]);

  async function send() {
    const text = input.trim();
    if (!text || busy) return;
    const next = [...msgs, { role: "user", content: text }];
    setMsgs(next); setInput(""); setBusy(true);
    try {
      const d = await api("/chat", { method: "POST", body: { messages: next } });
      setMsgs([...next, { role: "assistant", content: d.reply || "…" }]);
    } catch {
      setMsgs([...next, { role: "assistant", content: "Sorry, I hit a snag. Try again?" }]);
    } finally { setBusy(false); }
  }

  function saveCase() {
    // turn the chat into a transcript a lawyer can read
    const history = [];
    for (let i = 0; i < msgs.length; i++) {
      if (msgs[i].role === "user") {
        const a = msgs[i + 1]?.role === "assistant" ? msgs[i + 1].content : "";
        history.push({ question: msgs[i].content, answer: a });
      }
    }
    const firstUser = msgs.find((m) => m.role === "user")?.content || "Wants immigration help";
    onSave({
      goal: "Asked the AI assistant",
      history,
      assessment: {
        signal: "Needs a closer look",
        headline: "Came in through the AI assistant.",
        summary: firstUser.slice(0, 240),
        categories: [], prepare: [],
        disclaimer: "Preliminary chat, not legal advice. A lawyer will confirm options.",
      },
    });
  }

  const hasUserMsg = msgs.some((m) => m.role === "user");
  return (
    <>
      <style>{CSS}</style>
      <div className="wrap fullcol">
        <div className="chatHead">
          <button className="navlink" onClick={onBack}>← Home</button>
          <Logo />
          <span style={{ width: 48 }} />
        </div>
        <div className="bubbles">
          {msgs.map((m, i) => <div key={i} className={"bub " + (m.role === "user" ? "me" : "ai")}>{m.content}</div>)}
          {busy && <div className="bub ai"><span className="dots"><span /><span /><span /></span></div>}
          {hasUserMsg && !busy && (
            <div style={{ alignSelf: "flex-start", marginTop: 2 }}>
              {loggedIn
                ? <button className="btn line sm" onClick={onDashboard}>See my matched lawyers →</button>
                : <button className="btn amber sm" onClick={saveCase}>Save my case & see lawyers →</button>}
            </div>
          )}
          <div ref={endRef} />
        </div>
        <div className="composer">
          <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask anything about U.S. immigration…"
            onKeyDown={(e) => e.key === "Enter" && send()} />
          <button className="btn" onClick={send} disabled={busy || !input.trim()}>Send</button>
        </div>
      </div>
    </>
  );
}

/* ── Connect to a lawyer: add your questions, then sign up ────────────────── */
function Connect({ onContinue, onBack }) {
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");

  function add() {
    const t = text.trim();
    if (!t) return;
    setItems([...items, t]);
    setText("");
  }
  function remove(i) { setItems(items.filter((_, idx) => idx !== i)); }
  function cont() {
    const all = [...items];
    if (text.trim()) all.push(text.trim());
    if (all.length) onContinue(all);
  }
  const canContinue = items.length > 0 || text.trim().length > 0;

  return (
    <>
      <style>{CSS}</style>
      <div className="wrap stage">
        <div style={{ maxWidth: 600, width: "100%", margin: "0 auto" }}>
          <p className="eyebrow">Connect with a lawyer</p>
          <h2 className="qh">What do you want to ask a lawyer?</h2>
          <p className="qhelp">
            Add your questions or describe your situation. A matched lawyer will see this and reach out to you.
          </p>

          <div style={{ display: "flex", gap: 8, alignItems: "stretch" }}>
            <input className="otherInput" style={{ marginTop: 0, flex: 1, minWidth: 0 }} value={text}
              placeholder="e.g. Can I switch from F-1 to a work visa?"
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && add()} />
            <button className="btn line" style={{ flex: "none", padding: "0 18px" }} onClick={add} disabled={!text.trim()}>Add</button>
          </div>

          {items.length > 0 && (
            <div style={{ marginTop: 14 }}>
              {items.map((q, i) => (
                <div key={i} className="qa" style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start" }}>
                  <span>{q}</span>
                  <button className="navlink" style={{ flex: "none" }} onClick={() => remove(i)}>✕</button>
                </div>
              ))}
            </div>
          )}

          <div className="flowfoot" style={{ flexWrap: "wrap", gap: 10 }}>
            <button className="btn ghost" onClick={onBack}>← Back</button>
            <button className="btn amber" disabled={!canContinue} onClick={cont}>Continue →</button>
          </div>
        </div>
      </div>
    </>
  );
}

/* ── Signup (OTP) → account + case + lawyers ──────────────────────────────── */
function Signup({ goal, history, assessment, source, onDone, onBack }) {
  const [step, setStep] = useState("details");
  const [f, setF] = useState({ name: "", email: "", phone: "", country: "" });
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const sc = signalColor(assessment?.signal);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  async function sendCode() {
    if (!f.name.trim()) return setErr("Please add your name.");
    if (f.phone.replace(/\D/g, "").length < 7) return setErr("Enter a valid phone number.");
    setBusy(true); setErr("");
    const d = await api("/otp/send", { method: "POST", body: { phone: f.phone } });
    setBusy(false);
    if (d.ok) { setStep("otp"); setCode(""); } else setErr(d.error || "Couldn't send the code.");
  }
  async function verify() {
    if (code.trim().length < 4) return setErr("Enter the code we texted you.");
    setBusy(true); setErr("");
    const d = await api("/user/verify", { method: "POST", body: {
      phone: f.phone, code, name: f.name, email: f.email, country: f.country,
      goal, history, assessment, source, origin: window.location.origin,
    }});
    if (d.token) onDone(d);
    else { setErr(d.error || "That code didn't match."); setBusy(false); }
  }

  return (
    <>
      <style>{CSS}</style>
      <div className="wrap stage">
        <div style={{ maxWidth: 560, width: "100%", margin: "0 auto" }}>
          <p className="eyebrow">{step === "otp" ? "Verify your number" : "Save your case"}</p>
          {assessment && (
            <div style={{ margin: "8px 0 18px" }}>
              <span className="signal" style={{ color: sc.color, background: sc.bg }}>{assessment.signal}</span>
              <h2 className="qh" style={{ marginTop: 12 }}>{assessment.headline}</h2>
            </div>
          )}

          {step === "details" ? (
            <>
              <p className="qhelp">Verify your phone to save your case and see the lawyers we matched you with.</p>
              <div className="field"><label>Full name</label>
                <input value={f.name} onChange={set("name")} placeholder="Jane Doe" /></div>
              <div className="field"><label>Phone (we'll text a code)</label>
                <input value={f.phone} onChange={set("phone")} placeholder="+1 555 000 0000" /></div>
              <div className="field"><label>Email (optional)</label>
                <input value={f.email} onChange={set("email")} placeholder="jane@email.com" /></div>
              <div className="field"><label>Country of citizenship (optional)</label>
                <input value={f.country} onChange={set("country")} placeholder="e.g. India" /></div>
              {err && <p style={{ color: "var(--stamp)", fontSize: 14 }}>{err}</p>}
              <div className="flowfoot">
                <button className="btn ghost" onClick={onBack}>← Back</button>
                <button className="btn amber" disabled={busy} onClick={sendCode}>{busy ? "Sending…" : "Text me a code"}</button>
              </div>
            </>
          ) : (
            <>
              <p className="qhelp">We texted a 6-digit code to <b>{f.phone}</b>.</p>
              <div className="field"><label>Verification code</label>
                <input className="mono" inputMode="numeric" maxLength={6} autoFocus
                  style={{ letterSpacing: "0.4em", fontSize: 20 }} value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="••••••" /></div>
              {err && <p style={{ color: "var(--stamp)", fontSize: 14 }}>{err}</p>}
              <div className="flowfoot">
                <button className="btn ghost" onClick={() => { setStep("details"); setErr(""); }}>← Change number</button>
                <button className="btn amber" disabled={busy} onClick={verify}>{busy ? "Checking…" : "Verify & see lawyers"}</button>
              </div>
              <button className="btn ghost sm" onClick={sendCode} disabled={busy}>Resend code</button>
            </>
          )}
          <p style={{ fontSize: 12, color: "var(--muted)", marginTop: 18 }}>
            Verifying does not create an attorney–client relationship. SMS only; reply STOP to opt out.
          </p>
        </div>
      </div>
    </>
  );
}

/* ── Returning-user login (OTP) — for message deep-links ─────────────────── */
function ReturningLogin({ onDone, onBack }) {
  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function sendCode() {
    if (phone.replace(/\D/g, "").length < 7) return setErr("Enter a valid phone number.");
    setBusy(true); setErr("");
    const d = await api("/otp/send", { method: "POST", body: { phone } });
    setBusy(false);
    if (d.ok) { setStep("code"); setCode(""); } else setErr(d.error || "Couldn't send the code.");
  }
  async function verify() {
    setBusy(true); setErr("");
    const d = await api("/user/login", { method: "POST", body: { phone, code } });
    if (d.token) onDone(d);
    else { setErr(d.error || "That code didn't match."); setBusy(false); }
  }

  return (
    <>
      <style>{CSS}</style>
      <div className="wrap stage">
        <div style={{ maxWidth: 440, width: "100%", margin: "0 auto" }}>
          <Logo />
          <h2 className="qh" style={{ marginTop: 18 }}>Sign in to view your messages</h2>
          {step === "phone" ? (
            <>
              <div className="field"><label>Your phone number</label>
                <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 555 000 0000" /></div>
              {err && <p style={{ color: "var(--stamp)", fontSize: 14 }}>{err}</p>}
              <div className="flowfoot">
                <button className="btn ghost" onClick={onBack}>← Home</button>
                <button className="btn amber" disabled={busy} onClick={sendCode}>{busy ? "Sending…" : "Text me a code"}</button>
              </div>
            </>
          ) : (
            <>
              <p className="qhelp">We texted a code to <b>{phone}</b>.</p>
              <div className="field"><label>Code</label>
                <input className="mono" inputMode="numeric" maxLength={6} autoFocus
                  style={{ letterSpacing: "0.4em", fontSize: 20 }} value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="••••••" /></div>
              {err && <p style={{ color: "var(--stamp)", fontSize: 14 }}>{err}</p>}
              <div className="flowfoot">
                <button className="btn ghost" onClick={() => { setStep("phone"); setErr(""); }}>← Back</button>
                <button className="btn amber" disabled={busy} onClick={verify}>{busy ? "Checking…" : "Sign in"}</button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

/* ── User dashboard ───────────────────────────────────────────────────────── */
function Dashboard({ token, user, matched, ref_, assessment, onOpenThread, onHome, onLogout }) {
  const [lawyers, setLawyers] = useState(matched || []);
  const [threads, setThreads] = useState([]);

  useEffect(() => {
    if (!lawyers.length) api("/lawyers").then((d) => setLawyers((d.lawyers || []).slice(0, 6)));
    api("/threads", { token }).then((d) => setThreads(d.threads || []));
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <style>{CSS}</style>
      <div className="wrap" style={{ paddingTop: 22, paddingBottom: 60 }}>
        <nav className="nav">
          <Logo />
          <div className="navactions">
            <button className="navlink" onClick={onHome}>Home</button>
            <button className="navlink" onClick={onLogout}>Log out</button>
          </div>
        </nav>

        <h2 className="kick" style={{ marginTop: 8 }}>Welcome{user?.name ? `, ${user.name.split(" ")[0]}` : ""}.</h2>
        {ref_ && <p className="qhelp">Your case <b className="mono">{ref_}</b> is saved. {assessment?.summary}</p>}

        <div className="casebox" style={{ background: "var(--paper2)", marginTop: 18 }}>
          <b className="serif" style={{ fontSize: 16 }}>What happens next</b>
          <div style={{ marginTop: 4, color: "var(--ink2)" }}>
            Your profile is now visible to vetted lawyers who match your case. They'll review it and
            reach out to you here — you'll get a text the moment one messages you. No need to chase anyone.
          </div>
        </div>

        {threads.length > 0 && (
          <section style={{ marginTop: 22 }}>
            <span className="eyebrow">Lawyers who've reached out</span>
            <div style={{ marginTop: 12 }}>
              {threads.map((t) => (
                <button className="tcard" key={t.id} onClick={() => onOpenThread(t.id)}>
                  <Avatar p={t.counterpart} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <b className="serif" style={{ fontSize: 16 }}>{t.counterpart?.name || "Lawyer"}</b>
                    <div style={{ fontSize: 13, color: "var(--muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {t.lastText || "Opened a conversation →"}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </section>
        )}

        <section style={{ marginTop: 26 }}>
          <span className="eyebrow">Lawyers matched to your case</span>
          <h3 className="kick" style={{ fontSize: 24, marginTop: 4 }}>These attorneys can see your profile.</h3>
          <p className="qhelp">Matched on your goal and details. They reach out to you — sit tight.</p>
          <div className="lgrid" style={{ marginTop: 8 }}>
            {lawyers.map((l) => <LawyerCard key={l.id} l={l} />)}
          </div>
        </section>
      </div>
    </>
  );
}

/* ── Messaging thread (shared by user + lawyer) ──────────────────────────── */
function Thread({ token, threadId, role, onBack }) {
  const [messages, setMessages] = useState([]);
  const [meta, setMeta] = useState(null);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  async function load() {
    const d = await api(`/thread/${threadId}/messages`, { token });
    if (d.messages) { setMessages(d.messages); setMeta(d.meta); }
  }
  useEffect(() => {
    load();
    const iv = setInterval(load, 4000);
    return () => clearInterval(iv);
  }, [threadId, token]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  async function send() {
    const text = input.trim();
    if (!text || busy) return;
    setBusy(true); setInput("");
    setMessages((m) => [...m, { from: role, text, at: new Date().toISOString() }]);
    await api(`/thread/${threadId}/message`, { method: "POST", token, body: { text } });
    setBusy(false); load();
  }

  const cp = meta?.counterpart;
  const kase = meta?.case;
  const client = meta?.client;

  return (
    <>
      <style>{CSS}</style>
      <div className="wrap fullcol">
        <div className="chatHead">
          <button className="navlink" onClick={onBack}>← Back</button>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            {cp && <Avatar p={cp} />}
            <div>
              <b className="serif" style={{ fontSize: 16 }}>{cp?.name || "Conversation"}</b>
              {cp?.title && <div style={{ fontSize: 12, color: "var(--muted)" }}>{cp.title}</div>}
            </div>
          </div>
          <span style={{ width: 48 }} />
        </div>

        {(client || kase) && (
          <div className="casebox">
            {client && <div><b>{client.name}</b> · {client.country || "—"} · {client.phone || ""} {client.email || ""}</div>}
            {kase && <div style={{ marginTop: 4 }}><span className="tag">{kase.ref}</span> {kase.goal}{kase.signal ? ` · ${kase.signal}` : ""}</div>}
            {kase?.summary && <div style={{ marginTop: 4, color: "var(--muted)" }}>{kase.summary}</div>}
          </div>
        )}

        <div className="bubbles">
          {messages.length === 0 && <p className="qhelp">No messages yet — say hello 👋</p>}
          {messages.map((m, i) => <div key={i} className={"bub " + (m.from === role ? "me" : "ai")}>{m.text}</div>)}
          <div ref={endRef} />
        </div>
        <div className="composer">
          <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Type a message…"
            onKeyDown={(e) => e.key === "Enter" && send()} />
          <button className="btn" onClick={send} disabled={busy || !input.trim()}>Send</button>
        </div>
      </div>
    </>
  );
}

/* ─────────────────────────────────────────────────────────── LAWYER PORTAL ── */
function LawyerPortal() {
  useFonts();
  const [token, setToken] = useState(ls.get(LTOK) || "");
  const [lawyer, setLawyer] = useState(null);
  const [activeThread, setActiveThread] = useState(null);
  const [checking, setChecking] = useState(!!token);
  const deepThread = useMemo(() => new URLSearchParams(window.location.search).get("thread") || "", []);

  useEffect(() => {
    if (!token) { setChecking(false); return; }
    api("/me", { token }).then((d) => {
      if (d?.role === "lawyer") setLawyer(d.lawyer);
      else { ls.del(LTOK); setToken(""); }
      setChecking(false);
    });
  }, [token]);

  useEffect(() => {
    if (lawyer && deepThread && !activeThread) setActiveThread(deepThread);
  }, [lawyer]); // eslint-disable-line react-hooks/exhaustive-deps

  function onAuthed({ token: t, lawyer: l }) { ls.set(LTOK, t); setToken(t); setLawyer(l); }
  function logout() { ls.del(LTOK); setToken(""); setLawyer(null); }

  async function reachOut(userId) {
    const d = await api("/lawyer/thread/start", { method: "POST", token, body: { userId, origin: window.location.origin } });
    if (d.threadId) setActiveThread(d.threadId);
  }

  if (checking) return <><style>{CSS}</style><div className="wrap stage"><div className="dots"><span /><span /><span /></div></div></>;
  if (!lawyer) return <LawyerAuth onAuthed={onAuthed} />;
  if (activeThread)
    return <Thread token={token} threadId={activeThread} role="lawyer" onBack={() => setActiveThread(null)} />;
  return <LawyerDashboard token={token} lawyer={lawyer} onOpenThread={setActiveThread}
                          onReachOut={reachOut} onUpdated={setLawyer} onLogout={logout} />;
}

function LawyerAuth({ onAuthed }) {
  const [mode, setMode] = useState("login");
  const [f, setF] = useState({ name: "", email: "", password: "", title: "", location: "", specialties: "", rate: "", bio: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  async function go() {
    setBusy(true); setErr("");
    if (mode === "login") {
      const d = await api("/lawyer/login", { method: "POST", body: { email: f.email, password: f.password } });
      if (d.token) onAuthed(d); else { setErr(d.error || "Login failed."); setBusy(false); }
    } else {
      const d = await api("/lawyer/register", { method: "POST", body: {
        name: f.name, email: f.email, password: f.password, title: f.title, location: f.location,
        rate: f.rate, bio: f.bio, specialties: f.specialties.split(",").map((s) => s.trim()).filter(Boolean),
      }});
      if (d.token) onAuthed(d); else { setErr(d.error || "Could not register."); setBusy(false); }
    }
  }

  return (
    <>
      <style>{CSS}</style>
      <div className="wrap stage">
        <div style={{ maxWidth: 460, width: "100%", margin: "0 auto" }}>
          <Logo />
          <h2 className="qh" style={{ marginTop: 18 }}>Lawyer portal</h2>
          <p className="qhelp">Join the marketplace or sign in to message clients matched to your practice.</p>
          <div className="tabs">
            <button className={"tab" + (mode === "login" ? " on" : "")} onClick={() => setMode("login")}>Sign in</button>
            <button className={"tab" + (mode === "register" ? " on" : "")} onClick={() => setMode("register")}>Join</button>
          </div>

          {mode === "register" && (
            <>
              <div className="field"><label>Full name</label><input value={f.name} onChange={set("name")} /></div>
              <div className="field"><label>Headline (e.g. Work visas & EB green cards)</label><input value={f.title} onChange={set("title")} /></div>
              <div className="field"><label>Location</label><input value={f.location} onChange={set("location")} placeholder="City, State" /></div>
              <div className="field"><label>Specialties (comma separated: Employment, Family, Green Card, Student, Investor, Humanitarian)</label>
                <input value={f.specialties} onChange={set("specialties")} placeholder="Employment, Green Card" /></div>
              <div className="field"><label>Rate</label><input value={f.rate} onChange={set("rate")} placeholder="$200/hr · flat fees available" /></div>
              <div className="field"><label>Short bio</label><textarea value={f.bio} onChange={set("bio")} style={{ minHeight: 70 }} /></div>
            </>
          )}
          <div className="field"><label>Email</label><input value={f.email} onChange={set("email")} placeholder="you@firm.com" /></div>
          <div className="field"><label>Password</label><input type="password" value={f.password} onChange={set("password")} /></div>

          {err && <p style={{ color: "var(--stamp)", fontSize: 14 }}>{err}</p>}
          <button className="btn amber" style={{ width: "100%", marginTop: 6 }} disabled={busy} onClick={go}>
            {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Join the marketplace"}
          </button>
        </div>
      </div>
    </>
  );
}

function LawyerDashboard({ token, lawyer, onOpenThread, onReachOut, onUpdated, onLogout }) {
  const [threads, setThreads] = useState(null);
  const [leads, setLeads] = useState(null);
  const [editing, setEditing] = useState(false);
  const [busyId, setBusyId] = useState(null);
  useEffect(() => {
    api("/threads", { token }).then((d) => setThreads(d.threads || []));
    api("/lawyer/leads", { token }).then((d) => setLeads(d.leads || []));
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  async function reach(userId) { setBusyId(userId); await onReachOut(userId); setBusyId(null); }

  if (editing)
    return <LawyerProfileEditor token={token} lawyer={lawyer}
             onSaved={(l) => { onUpdated(l); setEditing(false); }} onCancel={() => setEditing(false)} />;

  return (
    <>
      <style>{CSS}</style>
      <div className="wrap" style={{ paddingTop: 22, paddingBottom: 60 }}>
        <nav className="nav">
          <Logo />
          <div className="navactions">
            <span className="navlink">{lawyer.name}</span>
            <button className="navlink" onClick={onLogout}>Log out</button>
          </div>
        </nav>

        <div style={{ display: "flex", gap: 14, alignItems: "center", marginTop: 8, justifyContent: "space-between", flexWrap: "wrap" }}>
          <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
            <Avatar p={lawyer} />
            <div>
              <h2 className="kick" style={{ fontSize: 26, margin: 0 }}>{lawyer.name}</h2>
              <div style={{ color: "var(--muted)", fontSize: 14 }}>{lawyer.title} · {lawyer.location}</div>
              <div style={{ marginTop: 4 }}>{(lawyer.specialties || []).map((s) => <span className="chip" key={s}>{s}</span>)}</div>
            </div>
          </div>
          <button className="btn line sm" onClick={() => setEditing(true)}>Edit profile</button>
        </div>

        <h3 className="kick" style={{ fontSize: 22, marginTop: 30 }}>New matched clients</h3>
        <p className="qhelp">People whose case fits your practice. Reach out to start the conversation.</p>
        {leads === null && <p className="qhelp">Loading…</p>}
        {leads && leads.length === 0 && <p className="qhelp">No new matches right now — check back soon.</p>}
        {leads && leads.map((l) => (
          <div className="row" key={l.caseId}>
            <div className="top">
              <div style={{ flex: 1 }}>
                <span className="tag">{l.ref}</span>{" "}
                <b className="serif" style={{ fontSize: 16 }}>{l.name}</b>{" "}
                <span style={{ color: "var(--muted)", fontSize: 13 }}>· {l.goal} · {l.country || "—"}</span>
                {l.summary && <div style={{ fontSize: 13.5, color: "var(--ink2)", marginTop: 5 }}>{l.summary}</div>}
              </div>
              <button className="btn amber sm" disabled={busyId === l.userId} onClick={() => reach(l.userId)}>
                {busyId === l.userId ? "Opening…" : "Reach out →"}
              </button>
            </div>
          </div>
        ))}

        <h3 className="kick" style={{ fontSize: 22, marginTop: 30 }}>Your clients</h3>
        {threads === null && <p className="qhelp">Loading…</p>}
        {threads && threads.length === 0 && (
          <p className="qhelp">No conversations yet. Reach out to a matched client above to get started.</p>
        )}
        {threads && threads.map((t) => (
          <button className="tcard" key={t.id} onClick={() => onOpenThread(t.id)}>
            <Avatar p={t.counterpart} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <b className="serif" style={{ fontSize: 16 }}>{t.client?.name || t.counterpart?.name || "Client"}</b>
              <div style={{ fontSize: 12.5, color: "var(--muted)" }}>
                {t.case ? `${t.case.ref} · ${t.case.goal}${t.case.signal ? " · " + t.case.signal : ""}` : ""}
              </div>
              <div style={{ fontSize: 13, color: "var(--muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {t.lastFrom === "lawyer" ? "You: " : ""}{t.lastText || "New conversation"}
              </div>
            </div>
          </button>
        ))}
      </div>
    </>
  );
}

function LawyerProfileEditor({ token, lawyer, onSaved, onCancel }) {
  const [f, setF] = useState({
    name: lawyer.name || "", title: lawyer.title || "", location: lawyer.location || "",
    rate: lawyer.rate || "", phone: lawyer.phone || "", bio: lawyer.bio || "",
    specialties: (lawyer.specialties || []).join(", "),
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  async function save() {
    setBusy(true); setErr("");
    const d = await api("/lawyer/me", { method: "POST", token, body: {
      name: f.name, title: f.title, location: f.location, rate: f.rate, phone: f.phone, bio: f.bio,
      specialties: f.specialties.split(",").map((s) => s.trim()).filter(Boolean),
    }});
    setBusy(false);
    if (d.lawyer) onSaved(d.lawyer); else setErr(d.error || "Could not save.");
  }

  return (
    <>
      <style>{CSS}</style>
      <div className="wrap" style={{ paddingTop: 22, paddingBottom: 60, maxWidth: 620 }}>
        <nav className="nav"><Logo /><button className="navlink" onClick={onCancel}>← Back</button></nav>
        <h2 className="kick" style={{ marginTop: 8 }}>Edit your profile</h2>
        <p className="qhelp">This is what clients see in the marketplace.</p>
        <div className="field"><label>Full name</label><input value={f.name} onChange={set("name")} /></div>
        <div className="field"><label>Headline</label><input value={f.title} onChange={set("title")} placeholder="Work visas & EB green cards" /></div>
        <div className="field"><label>Location</label><input value={f.location} onChange={set("location")} placeholder="City, State" /></div>
        <div className="field"><label>Specialties (comma separated)</label>
          <input value={f.specialties} onChange={set("specialties")} placeholder="Employment, Green Card" /></div>
        <div className="field"><label>Rate</label><input value={f.rate} onChange={set("rate")} placeholder="$200/hr · flat fees available" /></div>
        <div className="field"><label>Phone (for new-message texts)</label><input value={f.phone} onChange={set("phone")} placeholder="+1 555 000 0000" /></div>
        <div className="field"><label>Bio</label><textarea value={f.bio} onChange={set("bio")} style={{ minHeight: 90 }} /></div>
        {err && <p style={{ color: "var(--stamp)", fontSize: 14 }}>{err}</p>}
        <div className="flowfoot">
          <button className="btn ghost" onClick={onCancel}>Cancel</button>
          <button className="btn amber" disabled={busy} onClick={save}>{busy ? "Saving…" : "Save profile"}</button>
        </div>
      </div>
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────── ADMIN ── */
function Admin() {
  useFonts();
  const [authed, setAuthed] = useState(false);
  const [code, setCode] = useState("");
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("All");
  const initialCase = useMemo(() => new URLSearchParams(window.location.search).get("case") || "", []);
  const [q, setQ] = useState(initialCase);

  async function load() {
    try {
      const r = await fetch(`${API}/admin/cases`, { headers: { "x-admin-key": ADMIN_KEY } });
      setData(await r.json());
    } catch { setData({ cases: [], counts: {}, total: 0 }); }
  }
  useEffect(() => { if (authed) load(); }, [authed]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!authed) {
    return (
      <>
        <style>{CSS}</style>
        <div className="wrap stage">
          <div style={{ maxWidth: 360, margin: "0 auto" }}>
            <Logo />
            <h2 className="qh" style={{ marginTop: 18 }}>Admin access</h2>
            <input className="otherInput" placeholder="Access code" value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (code === "123123" || code === "999999") && setAuthed(true)} />
            <button className="btn amber" style={{ marginTop: 12, width: "100%" }}
              onClick={() => (code === "123123" || code === "999999") ? setAuthed(true) : alert("Invalid code")}>Enter</button>
          </div>
        </div>
      </>
    );
  }

  const cases = data?.cases || [];
  const ql = q.trim().toLowerCase();
  const shown = cases
    .filter((c) => filter === "All" || c.status === filter)
    .filter((c) => !ql || [c.ref, c.contact?.name, c.source, c.contact?.email].some((v) => (v || "").toLowerCase().includes(ql)));

  return (
    <>
      <style>{CSS}</style>
      <div className="wrap" style={{ paddingTop: 22, paddingBottom: 60 }}>
        <nav className="nav"><Logo /><span className="navlink">Admin console</span></nav>
        <h2 className="kick" style={{ marginTop: 10 }}>Cases ({data?.total || 0})</h2>
        <div className="adminbar">
          <button className="pill" onClick={() => setFilter("All")}>All · {data?.total || 0}</button>
          {STATUSES.map((s) => (
            <button key={s} className="pill" onClick={() => setFilter(s)}>{s} · {data?.counts?.[s] || 0}</button>
          ))}
          <button className="pill" onClick={load}>↻ Refresh</button>
          <input className="otherInput" style={{ width: 220, padding: "7px 12px", margin: 0 }}
            placeholder="Search ref / name / source" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        {shown.length === 0 && <p className="qhelp">No cases here yet.</p>}
        {shown.map((c) => <CaseRow key={c._id} c={c} onChange={load} />)}
      </div>
    </>
  );
}

function CaseRow({ c, onChange }) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(c.status);
  const [note, setNote] = useState("");
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);
  const sc = signalColor(c.assessment?.signal);

  async function save() {
    setBusy(true);
    await fetch(`${API}/admin/case/${c._id}`, {
      method: "POST", headers: { "content-type": "application/json", "x-admin-key": ADMIN_KEY },
      body: JSON.stringify({ status, note, notifyApplicant: notify }),
    });
    setNote(""); setBusy(false); onChange();
  }

  return (
    <div className="row">
      <div className="top">
        <div>
          <span className="tag">{c.ref}</span>{" "}
          <b className="serif" style={{ fontSize: 17 }}>{c.contact?.name}</b>{" "}
          <span style={{ color: "var(--muted)", fontSize: 13 }}>· {c.goal}</span>
          <div style={{ fontSize: 13, color: "var(--muted)", marginTop: 4 }}>
            {c.contact?.country || "—"} · {c.contact?.email || ""} {c.contact?.phone || ""}
          </div>
          {c.source && <span className="tag" style={{ marginTop: 6, display: "inline-block" }}>src: {c.source}</span>}
        </div>
        <span className="signal" style={{ color: sc.color, background: sc.bg }}>{c.assessment?.signal || c.status}</span>
      </div>
      <button className="btn ghost sm" onClick={() => setOpen(!open)}>{open ? "Hide details ▲" : "View intake & respond ▼"}</button>
      {open && (
        <div style={{ marginTop: 8 }}>
          {c.assessment?.summary && <p style={{ fontSize: 14, lineHeight: 1.6 }}>{c.assessment.summary}</p>}
          {(c.assessment?.categories || []).map((x, i) => <div key={i} style={{ fontSize: 13.5 }}>• <b>{x.name}</b> — {x.why}</div>)}
          <p className="eyebrow" style={{ marginTop: 14 }}>Intake transcript</p>
          {(c.transcript || []).map((t, i) => <div className="qa" key={i}><b>{t.question}</b><br />{t.answer}</div>)}
          {(c.notes || []).length > 0 && (
            <>
              <p className="eyebrow" style={{ marginTop: 14 }}>Notes</p>
              {c.notes.map((n, i) => <div key={i} style={{ fontSize: 13, color: "var(--muted)" }}>— {n.text}</div>)}
            </>
          )}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginTop: 16 }}>
            <select value={status} onChange={(e) => setStatus(e.target.value)}>{STATUSES.map((s) => <option key={s}>{s}</option>)}</select>
            <label style={{ fontSize: 13, color: "var(--muted)", display: "flex", gap: 5, alignItems: "center" }}>
              <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} /> Text the applicant
            </label>
          </div>
          <textarea style={{ width: "100%", marginTop: 10, minHeight: 64 }} placeholder="Note to applicant / internal note…"
            value={note} onChange={(e) => setNote(e.target.value)} />
          <button className="btn amber sm" style={{ marginTop: 8 }} disabled={busy} onClick={save}>{busy ? "Saving…" : "Update case"}</button>
        </div>
      )}
    </div>
  );
}